import { Check } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export interface TimelineStep {
  id: string;
  label: string;
  status: 'completed' | 'current' | 'pending';
  date?: string;
  subStatusLabel?: string;
}

interface TimelineProps {
  steps: TimelineStep[];
  loading?: boolean;
  error?: string;
  title?: string;
}

const StepIcon = ({ status }: { status: TimelineStep['status'] }) => {
  if (status === 'completed') {
    return (
      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
        <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
      </div>
    );
  }

  if (status === 'current') {
    return (
      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
        <div className="w-2.5 h-2.5 rounded-full bg-white" />
      </div>
    );
  }

  return (
    <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0 bg-white" />
  );
};

const Connector = ({ status }: { status: TimelineStep['status'] }) => (
  <div
    className={`w-0.5 flex-1 min-h-[32px] ${
      status === 'completed' ? 'bg-primary' : 'bg-gray-300'
    }`}
  />
);

const TimelineSkeleton = ({ count = 3 }: { count?: number }) => (
  <div className="border border-gray-200 rounded-lg p-6">
    <div className="space-y-0">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <Skeleton className="w-6 h-6 rounded-full flex-shrink-0" />
            {i < count - 1 && (
              <Skeleton className="w-0.5 flex-1 min-h-[32px]" />
            )}
          </div>
          <div className="pb-4">
            <Skeleton className="h-4 w-24 mb-1" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Timeline = ({ steps, loading, error, title }: TimelineProps) => {
  if (loading) return <TimelineSkeleton count={steps.length || 3} />;

  if (error) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="text-center py-4">
          <p className="text-red-600 mb-2">Failed to load timeline data</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6">
      {title && <h3 className="text-gray-900 mb-6">{title}</h3>}
      <div className="space-y-0">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          return (
            <div key={step.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <StepIcon status={step.status} />
                {!isLast && <Connector status={step.status} />}
              </div>
              <div className={isLast ? '' : 'pb-4'}>
                <div className="text-sm text-gray-900">{step.label}</div>
                {step.date && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {step.date}
                  </div>
                )}
                {step.subStatusLabel && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {step.subStatusLabel}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;
